import React from "react";
import {
  TrendingUp,
  PieChart,
  ArrowLeftRight,
  ScrollText,
  Globe,
  Bitcoin,
  Building2,
  FileText,
  FileSearch,
  Database,
  Newspaper,
  Leaf,
  Award,
  Share2,
  MessageCircle,
  Activity,
  ArrowRight,
} from "lucide-react";

const GROUPS = [
      { icon: TrendingUp, title: "Stocks", description: "Daily & historical stock data.", href: "/products/stocks", cta: "Explore stocks" },
      { icon: PieChart, title: "ETFs", description: "ETF holdings & constituent weights.", href: "/products/etfs", cta: "Explore ETFs" },
      { icon: ArrowLeftRight, title: "Forex", description: "Currency pair rates with deep history.", href: "/products/forex", cta: "Explore forex" },
      { icon: ScrollText, title: "Futures", description: "Contract specs & pricing.", href: "/products/futures", cta: "Explore futures" },
      { icon: Globe, title: "Indices", description: "Global benchmarks & exposure.", href: "/products/indices", cta: "Explore indices" },
      { icon: Bitcoin, title: "Crypto", description: "Digital asset price data.", href: "/products/crypto", cta: "Explore crypto" },
      { icon: Building2, title: "Profiles", description: "Company profiles & fundamentals.", href: "/products/corporate-profiles", cta: "Explore profiles" },
      { icon: FileText, title: "Earnings", description: "Earnings data & analyst estimates.", href: "/products/earnings", cta: "Explore earnings" },
      { icon: FileSearch, title: "Disclosures", description: "SEC filings & financial statements.", href: "/products/disclosures", cta: "Explore disclosures" },
      { icon: Database, title: "Macros", description: "Broad economic datasets.", href: "/products/economics", cta: "Explore macros" },
      { icon: Newspaper, title: "News", description: "News sentiment by company & sector.", href: "/products/news", cta: "Explore news" },
      { icon: Leaf, title: "ESG", description: "Sustainability ratings.", href: "/products/esg", cta: "Explore ESG" },
      { icon: Award, title: "Credit", description: "Ratings across entity types.", href: "/products/credit", cta: "Explore credit" },
      { icon: Share2, title: "Supply Chain", description: "Supply chain data, peers & customers.", href: "/products/supply-chain", cta: "Explore supply chain" },
      { icon: MessageCircle, title: "Sentiment", description: "Aggregated sentiment data.", href: "/products/sentiment", cta: "Explore sentiment" },
      { icon: Activity, title: "Web Traffic", description: "Digital presence & engagement.", href: "/products/webtraffic", cta: "Explore web traffic" },

 ];

export default function ProductSuite() {
  return (
    <section className="sqp">
      <style>{`
        .sqp {
          position: relative;
          overflow: visible;
          padding: 96px 64px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .sqp-decor {
          position: absolute;
          top: -120px;
          right: -30vw;
          width: 900px;
          height: 900px;
          pointer-events: none;
        }
        .sqp-header {
          position: relative;
          z-index: 1;
          max-width: 640px;
        }
        .sqp-eyebrow {
          font-size: 15px;
          color: rgba(var(--blackr), 0.7);
          margin: 0 0 20px;
        }
        .sqp-title {
          font-size: 56px;
          line-height: 1.1;
          color: var(--black);
          margin: 0 0 40px;
          font-weight: 400;
        }
        .sqp-divider {
          border: none;
          border-top: 1px dashed rgba(var(--greyr), 0.4);
          margin: 0 0 32px;
        }
        .sqp-lede {
          font-size: 19px;
          line-height: 1.5;
          color: var(--grey);
          margin: 0;
          max-width: 460px;
        }
        .sqp-groups {
          position: relative;
          z-index: 1;
          margin-top: 88px;
          display: flex;
          flex-direction: column;
          gap: 56px;
        }
        .sqp-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 48px;
        }
        @media (max-width: 900px) {
          .sqp-grid { grid-template-columns: repeat(2, 1fr); }
          .sqp { padding: 64px 24px; }
          .sqp-title { font-size: 38px; }
        }
        .sqp-item-head {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }
        .sqp-icon-box {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: var(--white);
          border: 1px solid rgba(var(--greyr), 0.25);
          box-shadow: 0 1px 2px rgba(var(--blackr), 0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sqp-item-title {
          font-size: 16px;
          color: var(--black);
        }
        .sqp-item-desc {
          font-size: 14px;
          line-height: 1.45;
          color: var(--grey);
          margin: 0 0 12px;
        }
        .sqp-cta {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          text-decoration: none;
        }
        .sqp-cta:hover {
          text-decoration: underline;
        }
        .sqp-cta svg {
          transition: transform 0.15s ease;
        }
        .sqp-cta:hover svg {
          transform: translateX(2px);
        }
      `}</style>

      <svg className="sqp-decor" viewBox="0 0 900 900" fill="none">
        <circle cx="450" cy="450" r="140" stroke="rgba(141,141,141,0.4)" strokeDasharray="3 5" />
        <circle cx="450" cy="450" r="300" stroke="rgba(141,141,141,0.4)" strokeDasharray="3 5" />
        <circle cx="450" cy="450" r="440" stroke="rgba(141,141,141,0.4)" strokeDasharray="3 5" />
        <circle cx="310" cy="450" r="4" fill="rgba(141,141,141,0.6)" />
        <circle cx="450" cy="450" r="4" fill="var(--purple)" />
        <circle cx="750" cy="450" r="4" fill="rgba(141,141,141,0.6)" />
        <circle cx="770" cy="110" r="4" fill="var(--purple)" />
        <circle cx="700" cy="800" r="4" fill="#8fc4ff" />
      </svg>

      <div className="sqp-header">
        <p className="sqp-eyebrow">Product Suite</p>
        <h2 className="sqp-title serif">Get to know Axion</h2>
        <hr className="sqp-divider" />
        <p className="sqp-lede">
          Replace fragmented tools with AxionQuant, the financial data API that helps you run financial analysis at the
          speed of thought.
        </p>
      </div>

      <div className="sqp-groups">
        <div className="sqp-grid">
              {GROUPS.map(({ icon: Icon, title, description, href, cta }) => (
                <div key={title}>
                  <div className="sqp-item-head">
                    <div className="sqp-icon-box">
                      <Icon size={18} color="var(--black)" strokeWidth={1.75} />
                    </div>
                    <span className="sqp-item-title">{title}</span>
                  </div>
                  <p className="sqp-item-desc">{description}</p>
                  <a className="sqp-cta" href={href}>
                    {cta}
                    <ArrowRight size={14} strokeWidth={2} />
                  </a>
                </div>
              ))}
            </div>
      </div>
    </section>
  );
}
